// TEBHOS SPA entry (rspack). Next.js root layout'un yerini alır:
// globals.css + i18n init + provider ağacı (App) + router.

import "@app/app/globals.css";
// referans-parite: renk sistemi globals'tan sonra yüklenir (index.tsx import sırası).
import "@app/features/theme/styles/generated/color-system.css";
// Token-kanon 1-1 paritesi (kaynak-referans THEME_VARIABLES 333 token manifesti):
// color-system.css'in eli-üretmediği ama kaynak-referansta var olan token TANIMLARI
// (ör. --code-*/--ansi-*/--markup-*/--alert-*/--textarea-*). Elle yazıldı
// (üretici script'in GİRDİSİ, generated/ çıktısı değil), globals+color-system
// SONRASI, runtime custom-theme enjeksiyonundan (_themeManager applyCustomCss,
// document.head'e en-son-ekleme) ÖNCE yüklenir — bkz. base-tokens.css başlığı.
import "@app/features/theme/styles/base-tokens.css";
// referans-parite: mesaj-düzeni CSS'i (--message-* değişkenler + kompakt
// markdown kuralları) renk sisteminden sonra yüklenir. Üretici: FX2'nin
// scripts/GenerateMessageLayoutCss.ts + package.json "generate:message-layout"
// script'i (bkz. theme/layout/MessageLayoutCss.ts renderMessageLayoutCss()).
// kaynak-referans import sırası (index.tsx): globals → color-system → message-layout → preflight.
import "@app/features/theme/styles/generated/message-layout.css";
import "@app/features/theme/styles/preflight.css";

// ZORUNLU İLK-SIRA: localStorage zustand-kalıntı migrasyonu (sarmalı format →
// düz + eski anahtar adları → kaynak-referans-hizalı) store singleton'ları import
// edilmeden ÖNCE koşmalı — store'lar import-anında hidre olur.
import "@app/features/platform/state/PersistedStorageMigration";

import { setupHttp } from "@app/app/SetupHttp";
import { installAssetPreconnects } from "@app/features/platform/utils/AssetPreconnect";
import { installLegacySettingsLiveSync } from "@app/features/user/utils/LegacySettingsLiveSync";
import { createRoot } from "react-dom/client";
import { i18n } from "@lingui/core";
// i18n-1-1 CUTOVER (2026-07-29): `App` STATİK import EDİLMEZ — Kaynak
// index.tsx:130 paritesi (loadLazyModule ile `initI18n()` SONRASI import).
// Statik import tüm modül grafiğini katalog aktive olmadan değerlendirir ve
// modül-kapsamı `i18n._()` çağrıları "without setting a locale" ile patlar
// (canlı yaşandı). Aşağıdaki async boot'ta dinamik import edilir.

// kaynak-referans index.tsx:176 — portlanan http katmanına auth-provider kablosu
// (yokken tüm RestTransport istekleri 401 alıyordu; bkz. SetupHttp.ts).
setupHttp();
// Görsel/medya sunucularına el sıkışma ilk istekten ÖNCE başlasın (kaynak-referans index.html preconnect karşılığı).
installAssetPreconnects();
// Eski ayar sekmelerinin cihazlar arası anlık eşitlemesi (ayarlar kapalıyken de tema/dil uygulansın).
installLegacySettingsLiveSync();
import { initializeEmojiParser } from "@app/features/messaging/utils/markdown/EmojiProviderSetup";

initializeEmojiParser();

// Dev (localhost): önceki sürümlerden kalan service worker'lar eski/stale build
// chunk'larını cache'leyip donmalara ("wait until bundle finished") ve stale
// modül hatalarına ("apiFetch is not a function") yol açabiliyor. Mevcut SW'leri
// kaldır + tüm cache'leri temizle. Prod'a (gerçek domain) dokunmaz.
if (
  typeof window !== "undefined" &&
  (location.hostname === "localhost" || location.hostname === "127.0.0.1")
) {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => regs.forEach((r) => r.unregister()))
      .catch(() => {});
  }
  if (typeof caches !== "undefined") {
    caches
      .keys()
      .then((keys) => keys.forEach((k) => caches.delete(k)))
      .catch(() => {});
  }
}

const container = document.getElementById("root");
if (!container) throw new Error("Missing #root element");

// i18n-1-1 CUTOVER (2026-07-29, Kaynak index.tsx:184-186 paritesi): katalog
// YÜKLENMEDEN render başlamaz — kaynak-referans `await initI18n()` sonra mount eder.
// Eski TEBHOS I18nProvider (senkron i18n.load + reload'lu switch) SÖKÜLDÜ;
// aktivasyon artık tek yerden: app/I18n.ts (reactive proxy + lazy katalog).
void (async () => {
  const i18nModule = await import("@app/app/I18n");
  const { initI18n } = i18nModule;
  const reactiveI18n = i18nModule.default;
  // Watchdog modülünün indirilmesi katalog yüklemesiyle (initI18n — ağ) PARALEL koşar;
  // eskiden art arda bekleniyordu. Kurulum yine katalog aktive olduktan SONRA yapılır.
  // App burada paralele ALINMAZ: modül grafiği katalogdan önce değerlendirilirse
  // modül-kapsamı `i18n._()` çağrıları patlar (yukarıdaki not), boot modülleri de
  // App'ten önce değerlendirilemez (aşağıdaki SIRA KRİTİK notu).
  const [{ installLocaleSwitchWatchdog }] = await Promise.all([
    import("@app/features/i18n/utils/LocaleSwitchWatchdog"),
    initI18n(),
  ]);
  installLocaleSwitchWatchdog();
  const { App } = await import("./App");
  // Kaynak index.tsx:167-171 paritesi — React-dışı singleton'lara reaktif i18n
  // kablosu. ⭐ EKSİKTİ (2026-07-30 canlı vaka): ChannelDisplayName.setI18n hiç
  // çağrılmadığından İLK GRUP-DM oluşturulduğunda computeGroupDMDisplayName
  // "i18n not initialized" fırlatıyor, istisna READY hidrasyon reaction'ını
  // ortasından kesiyordu → servers:0/relationships:1 (arkadaşlar "Arkadaş ekle"
  // görünür, DM arama butonları kaybolur, sunucular boşalır).
  // ⚠ SIRA KRİTİK (2026-07-30 ikinci canlı vaka): bu import'lar App'ten ÖNCE
  // yapılınca modül-başlatma sırası değişiyor ve döngüsel importlarda bazı
  // bileşen exportları undefined yakalanıyordu ("Element type is invalid" —
  // sağ-tık menüsü + arama-UI çöküyordu). App İLK import edilir (kanıtlanmış
  // sıra), bu modüller zaten grafiğin içinde olduğundan buradaki import'lar
  // sıfır-maliyetli olur; setI18n yine İLK render'dan önce koşar. kaynak-referanstaki
  // Keybind/CaptchaInterceptor kabloları TEBHOS'ta bilinçli yok (farklı uyarlandı).
  const [
    { default: QuickSwitcher },
    { default: ChannelDisplayName },
    { default: NewDeviceMonitoring },
    { default: Notification },
    { default: StatusPage },
    { AppErrorBoundary },
    { ErrorFallback },
    { I18nProvider },
  ] = await Promise.all([
    import("@app/features/search/state/QuickSwitcher"),
    import("@app/features/channel/state/ChannelDisplayName"),
    import("@app/features/auth/state/NewDeviceMonitoring"),
    import("@app/features/ui/state/Notification"),
    import("@app/features/user/state/StatusPage"),
    // Hata sınırı + çökme ekranı da App'ten SONRA yüklenir (yukarıdaki SIRA
    // KRİTİK notu): statik import, modül grafiğini katalog etkinleşmeden
    // değerlendirirdi.
    import("@app/features/app/components/AppErrorBoundary"),
    import("@app/features/app/components/ErrorFallback"),
    import("@lingui/react"),
  ]);
  QuickSwitcher.setI18n(reactiveI18n);
  ChannelDisplayName.setI18n(reactiveI18n);
  NewDeviceMonitoring.setI18n(reactiveI18n);
  Notification.setI18n(reactiveI18n);
  // kaynak-referans index.tsx:173-174 — durum-sayfası dalgası: SplashScreen'in
  // "bağlantı sorunları" satırı zaten checkIncidents() çağırıyor (10sn gecikmeli)
  // ama polling (ScheduledMaintenanceNagbar + gelecekteki olaylar) boot'ta
  // başlamazsa hiç güncellenmez. Kendi-barındırılan kurulumlarda no-op
  // (StatusPage.startPolling/checkIncidents içi RuntimeConfig.isSelfHosted() kapısı).
  void StatusPage.checkIncidents();
  StatusPage.startPolling();
  // Kaynak-referans index.tsx mountRoot paritesi: kök ağaç hata sınırıyla
  // sarılır. Sınır yokken tek bir render hatası tüm ağacı söküp beyaz ekran
  // bırakıyordu. Çökme ekranı App'in kendi I18nProvider'ının DIŞINDA
  // çizildiği için kendi sağlayıcısıyla sarılır (aynı etkin i18n örneği).
  createRoot(container).render(
    <AppErrorBoundary
      fallback={(error) => (
        <I18nProvider i18n={i18n}>
          <ErrorFallback error={error ?? undefined} data-flx="index.render-app.error-fallback" />
        </I18nProvider>
      )}
      data-flx="index.render-app.app-error-boundary"
    >
      <App />
    </AppErrorBoundary>,
  );
})();
